import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Participant, Task } from '../types';
import { XIcon, BellIcon } from './icons';

interface NudgeModalProps {
    participant?: Participant;
    targetName?: string;
    isOpen: boolean;
    onClose: () => void;
    onSend: (message: string, taskId?: string) => void;
    tasks?: Task[];
}

const NudgeModal: React.FC<NudgeModalProps> = ({ participant, targetName, isOpen, onClose, onSend, tasks = [] }) => {
    const [message, setMessage] = useState("Please complete your pending tasks!");
    const [selectedTaskId, setSelectedTaskId] = useState<string>('');

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
                {/* Header */}
                <div className="bg-action px-6 py-4 flex justify-between items-center text-white rounded-t-2xl">
                    <h3 className="font-bold flex items-center gap-2 text-lg">
                        <BellIcon className="w-5 h-5" />
                        Send Nudge to {targetName || participant?.name}
                    </h3>
                    <button onClick={onClose} className="hover:bg-white/20 p-1 rounded-full transition-colors">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                    {tasks.length > 0 && (
                        <div>
                            <label className="block text-xs font-medium text-text-muted mb-2">Link to Task (Optional)</label>
                            <select
                                value={selectedTaskId}
                                onChange={(e) => setSelectedTaskId(e.target.value)}
                                className="w-full border border-app-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-action focus:ring-1 focus:ring-action transition-colors bg-white"
                            >
                                <option value="">General nudge — no specific task</option>
                                {tasks.map(t => (
                                    <option key={t.id} value={t.id}>{t.title || t.prompt}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div>
                        <label className="block text-xs font-medium text-text-muted mb-2">Custom Message</label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="w-full border border-app-border rounded-lg p-3 text-sm focus:outline-none focus:border-action focus:ring-1 focus:ring-action transition-colors min-h-[100px]"
                            placeholder="Write a nudge message..."
                        />
                    </div>

                    {/* Preview */}
                    <div className="bg-app-bg rounded-xl p-4 border border-app-border">
                        <p className="text-xs font-bold text-text-faint uppercase tracking-wider mb-2">Participant Preview</p>
                        <div className="bg-white rounded-lg shadow-sm p-3 flex items-start gap-3 border border-app-border">
                            <div className="bg-action rounded-full p-1.5 shrink-0">
                                <BellIcon className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-text-ink">New Nudge from Research Team</p>
                                <p className="text-sm text-text-muted line-clamp-2" style={{ textWrap: 'pretty' }}>{message}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-app-border flex justify-end gap-3 bg-app-bg rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-text-muted hover:bg-app-bg rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSend(message, selectedTaskId || undefined)}
                        className="px-6 py-2 text-sm font-medium text-white bg-action hover:bg-action/90 rounded-lg active:scale-95 transition-colors"
                    >
                        Send Nudge
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default NudgeModal;
